import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import logoWordmark from "@/assets/brand/ybr-wordmark.png";
import { Loader2, Eye, EyeOff } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";

const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [isSignUp, setIsSignUp] = useState(false);
  const [forgotPassword, setForgotPassword] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [rememberPassword, setRememberPassword] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();

  // Auto-redirect se já autenticado
  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session) {
        navigate('/');
      }
    });
  }, [navigate]);

  const handleAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (forgotPassword) {
        const { error } = await supabase.auth.resetPasswordForEmail(email, {
          redirectTo: 'com.youbetterrun.app://auth-callback',
        });

        if (error) throw error;

        toast({
          title: "Email enviado!",
          description: "Verifique sua caixa de entrada para redefinir a senha.",
        });
        setForgotPassword(false);
      } else if (isSignUp) {
        const { error } = await supabase.auth.signUp({
          email,
          password,
          options: {
            emailRedirectTo: 'com.youbetterrun.app://auth-callback',
          },
        });

        if (error) throw error;

        toast({
          title: "Conta criada!",
          description: "Bem-vindo ao You Better Run! 🏃",
        });
      } else {
        const { error } = await supabase.auth.signInWithPassword({
          email,
          password,
        });

        if (error) throw error;

        navigate('/');
      }
    } catch (error: any) {
      let errorMessage = error.message;
      
      if (error.message.includes('Invalid login credentials')) {
        errorMessage = 'Email ou senha incorretos';
      } else if (error.message.includes('Email not confirmed')) {
        errorMessage = 'Por favor, confirme seu email primeiro';
      } else if (error.message.includes('User already registered')) {
        errorMessage = 'Este email já está cadastrado';
      }

      toast({
        title: "Erro",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0A0A0A] dark-premium px-4">
      <div className="w-full max-w-md space-y-8 animate-fade-in">
        <div className="text-center">
              <img 
                src={logoWordmark} 
                alt="You Better Run" 
                className="h-72 w-72 mx-auto mb-8"
                width={288}
                height={288}
                style={{ aspectRatio: '1/1' }}
              />
          <div className="space-y-2">
            {isSignUp && (
              <>
                <h1 className="text-3xl font-bold">Começar Jornada</h1>
                <p className="text-muted-foreground mt-6">
                  O primeiro app que acolhe antes de desafiar
                </p>
              </>
            )}
          </div>
            </div>

            <form onSubmit={handleAuth} className="space-y-6 mt-8">
              <div className="space-y-4">
                <Input
                  type="email"
                  placeholder="Email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="h-12 bg-muted/30 border-border focus:border-primary/50 focus:ring-2 focus:ring-primary/20 backdrop-blur-sm"
                />
                {!forgotPassword && (
                  <div className="space-y-2">
                    <div className="relative">
                      <Input
                        type={showPassword ? "text" : "password"}
                        placeholder="Senha"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required={!forgotPassword}
                        className="h-12 bg-muted/30 border-border focus:border-primary/50 focus:ring-2 focus:ring-primary/20 backdrop-blur-sm pr-10"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                      >
                        {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                    {!isSignUp && (
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-2">
                          <Checkbox 
                            id="remember" 
                            checked={rememberPassword}
                            onCheckedChange={(checked) => setRememberPassword(checked as boolean)}
                          />
                          <Label 
                            htmlFor="remember" 
                            className="text-xs text-muted-foreground cursor-pointer"
                          >
                            Lembrar senha
                          </Label>
                        </div>
                        <button
                          type="button"
                          onClick={() => setForgotPassword(true)}
                          className="text-xs text-muted-foreground hover:text-primary transition-colors"
                        >
                          Esqueceu a senha?
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>

              <Button 
                type="submit" 
                className="w-full h-12 text-lg bg-gradient-primary hover:shadow-glow-primary transition-all duration-300"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                    Carregando...
                  </>
                ) : forgotPassword ? (
                  "Enviar Email de Recuperação"
                ) : isSignUp ? (
                  "Criar Conta"
                ) : (
                  "Entrar"
                )}
              </Button>

              <div className="space-y-2 text-center">
                {!forgotPassword ? (
                  <>
                    {!isSignUp ? (
                      <button
                        type="button"
                        onClick={() => navigate('/signup')}
                        className="text-sm text-primary hover:text-primary-glow transition-colors block w-full"
                      >
                        Não tem conta? Criar agora
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={() => setIsSignUp(false)}
                        className="text-sm text-primary hover:text-primary-glow transition-colors block w-full"
                      >
                        Já tem conta? Entrar
                      </button>
                    )}
                  </>
                ) : (
                  <button
                    type="button"
                    onClick={() => setForgotPassword(false)}
                    className="text-sm text-primary hover:text-primary-glow transition-colors"
                  >
                    Voltar ao login
                  </button>
                )}
              </div>

              <div className="text-xs text-muted-foreground text-center pt-4 border-t border-glass-border">
                Ao criar conta, você concorda com nossos{' '}
                <a href="/terms" className="text-primary hover:underline">Termos de Uso</a>
                {' '}e{' '}
                <a href="/privacy" className="text-primary hover:underline">Política de Privacidade</a>
              </div>
            </form>
        </div>
      </div>
  );
};

export default Login;
